import React, { FunctionComponent, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { Button, DesignLanguage, Heading, Icon, Link, Modal, Text, useIsBreakpointLargeAndUp, useIsDesignLanguage } from '@afterpaytouch/core'
import styles from '../PayMonthlyOnly/style.module.css'
import { BASE_PATH } from '../../env'
import Image from 'next/image'
import { Terms } from '../PaymentTypes/PaymentTypesPBIRejected/Terms'
import { PayMonthlyOnlyPoint } from './PayMonthlyOnlyPoint/PayMonthlyOnlyPoint'
import { TrackingEvent } from '../../model/monitoring'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { PayMonthlyOnlyContinueButton } from './PayMonthlyOnlyContinueButton/PayMonthlyOnlyContinueButton'

export const PayMonthlyOnly: FunctionComponent = () => {
  const { t } = useTranslation()
  const [showModal, setShowModal] = useState<boolean>(false)
  const isDesktop = useIsBreakpointLargeAndUp()
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const [hasSentModalOpenAnalyticsData, setHasSentModalOpenAnalyticsData] = useState(false)
  const [hasSentModalCloseAnalyticsData, setHasSentModalCloseAnalyticsData] = useState(false)

  const headerSvgSrc = [BASE_PATH, 'images/pay-monthly-only-lightweight-background.svg'].join('/')
  const iconSvgSrc = [BASE_PATH, 'images/pay-monthly-only-lightweight-icon.svg'].join('/')
  const modalIconSvgSrc = [BASE_PATH, 'images/pay-monthly-only-modal-icon.svg'].join('/')

  const showPbiDrawer = (): void => {
    if (!hasSentModalOpenAnalyticsData) {
      logEvent(TrackingEvent.PRESSED_PAY_MONTHLY_ONLY_LEARN_MORE_LINK)
      setHasSentModalOpenAnalyticsData(true)
    }
    setShowModal(true)
  }

  const handleClickClose = (): void => {
    if (!hasSentModalCloseAnalyticsData) {
      logEvent(TrackingEvent.CLOSED_PAY_MONTHLY_ONLY_PBI_MODAL)
      setHasSentModalCloseAnalyticsData(true)
    }
    setShowModal(false)
  }

  return (
    <>
      <div className={isDesktop ? styles.payMonthlyOnlyWrapperDesktop : styles.payMonthlyOnlyWrapperMobile}>
        <div className={styles.heroImage}>
          <Image
            data-testid='pay-monthly-only-scene'
            src={headerSvgSrc}
            alt=''
            width='720'
            height='406'
            style={{
              maxWidth: '100%',
              height: 'auto',
            }}
          />
          <div className={styles.overlay}>
            <Image
              data-testid='pay-monthly-only-icon'
              src={iconSvgSrc}
              alt=''
              width='80'
              height='80'
              style={{
                maxWidth: '100%',
                height: 'auto',
              }}
            />
          </div>
        </div>

        <div className={styles.payMonthlyOnlyBottomContainer}>
          <div className={styles.bottomContainerPaddingTop}>
            <div>
              <Text bold size={'XXL'} color={isArcadeDesignLanguage ? 'Standard' : undefined}>
                {t('consumerLending:payMonthlyOnly:title1')}
              </Text>
              <Text bold size={'XXL'} color={isArcadeDesignLanguage ? 'Standard' : undefined}>
                {t('consumerLending:payMonthlyOnly:title2')}
              </Text>
            </div>
            <div className={styles.secondaryHeader}>
              <Text color={isArcadeDesignLanguage ? 'Standard' : undefined}>
                {t('consumerLending:payMonthlyOnly:subtitle')}
                <div className={styles.learnMoreButton}>
                  <Link onClick={showPbiDrawer} testNameSpace='learn-more' kind='Emphasis'>
                    <Text bold color={isArcadeDesignLanguage ? 'Standard' : undefined}>
                      {t('consumerLending:payMonthlyOnly:learnMore')}
                    </Text>
                  </Link>
                </div>
              </Text>
            </div>

            <PayMonthlyOnlyPoint headerText={t('consumerLending:payMonthlyOnly:information:header1')} InformationIcon={Icon.Calendar} />
            <PayMonthlyOnlyPoint headerText={t('consumerLending:payMonthlyOnly:information:header2')} InformationIcon={Icon.Dollar} />
            <PayMonthlyOnlyPoint headerText={t('consumerLending:payMonthlyOnly:information:header3')} InformationIcon={Icon.PasswordHide} />

            <div className={styles.bottomContainer}>
              <div className={styles.termSpace}>
                <Terms />
              </div>
              <PayMonthlyOnlyContinueButton />
              <div className={styles.buttonsContainerText}>
                <Text size='XXS' color='Gray40'>
                  {t('consumerLending:payMonthlyOnly:term2')}
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal
        testNameSpace='pbi-not-available'
        data-testid='pbi-not-available-modal'
        show={showModal}
        onClose={handleClickClose}
        onBackdropClick={handleClickClose}
      >
        <div className={styles.modalContainer}>
          <div className={styles.modalIcon}>
            <Image
              data-testid='pay-monthly-only-modal-icon'
              src={modalIconSvgSrc}
              alt=''
              width='64'
              height='64'
              style={{
                maxWidth: '100%',
                height: 'auto',
              }}
            />
          </div>
          <Modal.Header>
            <div className={styles.modalHeading}>
              <Heading size={'XL'}>{t('consumerLending:payMonthlyOnly:pbiModal:heading')}</Heading>
            </div>
          </Modal.Header>
          <Modal.Content>
            <div className={styles.modalText}>
              <Text size={'M'}>{t('consumerLending:payMonthlyOnly:pbiModal:text')}</Text>
            </div>
            <Button kind='Secondary' padding='Fluid' onClick={handleClickClose} testNameSpace='pay-monthly-only-close-modal'>
              {t('consumerLending:payMonthlyOnly:close')}
            </Button>
          </Modal.Content>
        </div>
      </Modal>
    </>
  )
}
